import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Card,
  Col,
  CardHeader,
  CardBody,
  Container,
  Row,
  Table,
  Badge,
  CardFooter,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
  Modal,
  ModalBody,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  FormGroup,
  Label,
  Form,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from "reactstrap";
import { Bar } from "react-chartjs-2";
import Select from "react-select";
import { chartOptions } from "variables/charts.js";
import CommonHeader from "components/Headers/CommonHeader";
import Swal from "sweetalert2";
import {
  getAllUsers,
  getUserProfile,
  blockUserAPI,
  resetUserPasswordAPI,
  getReputationConfig,
  toggle2FA,
  getAllCountries,
  getAllStates,
  getAllCities,
  getAuditLogs
} from "api/ApiServices";
import Avatar from "../../components/Common/Avatar";
import SessionModal from "./SessionModal";





const filterSelectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: "0.375rem",
    minHeight: "43px",
    border: "0",
    boxShadow: "0 1px 3px rgba(50, 50, 93, 0.15), 0 1px 0 rgba(0, 0, 0, 0.02)",
    "&:hover": { boxShadow: "0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)" },
    fontSize: "0.875rem",
    background: "#fff",
    color: "#8898aa"
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#5e72e4" : state.isFocused ? "#f8f9fe" : "#fff",
    color: state.isSelected ? "#fff" : "#32325d",
    fontSize: "0.875rem",
    padding: "10px 15px",
    cursor: "pointer"
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  placeholder: (provided) => ({ ...provided, color: "#8898aa" }),
  singleValue: (provided) => ({ ...provided, color: "#32325d" })
};

const UserTable = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reputationTiers, setReputationTiers] = useState([]);
  const location = useLocation();
  const [userAuditLogs, setUserAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const highlightId = queryParams.get("searchId") || queryParams.get("highlight");
  const urlSearchText = queryParams.get("searchText") || "";
  const [searchTerm, setSearchTerm] = useState(urlSearchText);
  const [countries, setCountries] = useState([]);
  const [isCountriesLoading, setIsCountriesLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [isStatesLoading, setIsStatesLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [selectedUserForSessions, setSelectedUserForSessions] = useState(null);

  const toggleSessionModal = () => setSessionModalOpen(!sessionModalOpen);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);

  useEffect(() => {
    if (highlightId) {
      setTimeout(() => {
        const row = document.getElementById(`user-row-${highlightId}`);
        if (row) {
          row.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, [highlightId]);

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetData, setResetData] = useState({ newPassword: "", confirmPassword: "" });
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Function to toggle Reset Password modal and clear state on close
  const toggleResetModal = () => {
    if (resetModalOpen) {
      // If closing, clear the state
      setResetData({ newPassword: "", confirmPassword: "" });
      setResetError("");
      setShowPassword(false);
      setShowConfirmPassword(false);
      setResetLoading(false);
    }
    setResetModalOpen(!resetModalOpen);
  };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Filters
  const [countryFilter, setCountryFilter] = useState("All");
  const [stateFilter, setStateFilter] = useState("All");
  const [cityFilter, setCityFilter] = useState("All");
  const [tierFilter, setTierFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // Lookup refs for countries, states, and cities to prevent fetchUsers from changing
  // whenever the metadata lists are populated, avoiding double-reload flicker.
  const countriesRef = React.useRef([]);
  const statesRef = React.useRef([]);
  const citiesRef = React.useRef([]);

  useEffect(() => {
    countriesRef.current = countries;
  }, [countries]);

  useEffect(() => {
    statesRef.current = states;
  }, [states]);

  useEffect(() => {
    citiesRef.current = cities;
  }, [cities]);

  /* ═══════════════════════════════════════════════════
     SMART PAGINATION RENDERER
     ═══════════════════════════════════════════════════ */
  const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
    const totalPages = Math.ceil(totalItems / limit);
    if (totalPages <= 1) return null;

    const pages = [];
    const delta = 2; // Number of pages to show on either side

    // Always show first page
    pages.push(
      <PaginationItem key={1} active={currentPage === 1}>
        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
      </PaginationItem>
    );

    // Show start ellipsis if needed
    if (currentPage > delta + 2) {
      pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
    }

    // Show pages around current
    const rangeStart = Math.max(2, currentPage - delta);
    const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(
        <PaginationItem key={i} active={currentPage === i}>
          <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    // Show end ellipsis if needed
    if (currentPage < totalPages - delta - 1) {
      pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(
        <PaginationItem key={totalPages} active={currentPage === totalPages}>
          <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPages); }}>{totalPages}</PaginationLink>
        </PaginationItem>
      );
    }

    return pages;
  };

  const fetchUsers = React.useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const selectedCountry = countriesRef.current.find(c => c.c_uuid === countryFilter);
      const selectedState = statesRef.current.find(s => s.s_uuid === stateFilter);
      const selectedCity = citiesRef.current.find(ct => ct.ci_uuid === cityFilter);

      const countryName = selectedCountry ? selectedCountry.c_name : "All";
      const stateName = selectedState ? selectedState.s_name : "All";
      const cityName = selectedCity ? selectedCity.ci_name : "All";

      const response = await getAllUsers(page, pagination.limit, searchTerm, statusFilter, countryName, stateName, cityName, tierFilter);
      if (response && response.status) {
        setUsers(response.payload.users);
        setPagination({
          page: response.payload.page,
          limit: pagination.limit,
          total: response.payload.total,
          totalPages: response.payload.totalPages
        });
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      Swal.fire("Error", "Failed to fetch users from server.", "error");
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, countryFilter, stateFilter, cityFilter, tierFilter, pagination.limit]);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await getReputationConfig();
        if (res && res.status && res.payload && Array.isArray(res.payload.configs)) {
          const tierConfig = res.payload.configs.find(c => c.configKey === "TIER_LIMITS");
          if (tierConfig && Array.isArray(tierConfig.configValue)) {
            setReputationTiers(tierConfig.configValue);
          }
        }
      } catch (error) {
        console.error("Failed to fetch reputation tier config", error);
      }
    };
    fetchTiers();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchUsers(1);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchUsers]);

  const logAuditAction = (userId, action) => {
    fetchUsers(pagination.page);
  };

  const handleAction = async (action, user) => {
    if (action === "View Profile") {
      handleViewUser(user);
      return;
    }

    if (action === "Sessions") {
      setSelectedUserForSessions(user);
      setSessionModalOpen(true);
      return;
    }

    if (action === "Reset Password") {
      setSelectedUser(user);
      setResetModalOpen(true);
      return;
    }

    if (action === "Block") {
      const isBlocking = !user.is_blocked;

      if (isBlocking) {
        Swal.fire({
          title: `Block ${user.name}?`,
          text: "Please provide a reason for blocking this user (required):",
          input: 'textarea',
          inputPlaceholder: 'Reason for blocking...',
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#f5365c",
          confirmButtonText: "Yes, Block User",
          inputValidator: (value) => {
            if (!value) {
              return 'You need to write a reason to block a user!';
            }
          }
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              const reason = result.value || "Blocked by admin";
              const res = await blockUserAPI(user._id, reason, "block");
              if (res.status) {
                setUsers(prevUsers => prevUsers.map(u =>
                  u._id === user._id ? { ...u, is_blocked: true, active: false } : u
                ));
              }
            } catch (error) {
              Swal.fire("Error", "Something went wrong.", "error");
            }
          }
        });
      } else {
        Swal.fire({
          title: `Unblock ${user.name}?`,
          text: "Are you sure you want to unblock this user?",
          icon: "info",
          showCancelButton: true,
          confirmButtonColor: "#11cdef",
          confirmButtonText: "Yes, Unblock",
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              const res = await blockUserAPI(user._id, "Unblocked by admin", "unblock");
              if (res.status) {
                setUsers(prevUsers => prevUsers.map(u =>
                  u._id === user._id ? { ...u, is_blocked: false, active: true } : u
                ));
              }
            } catch (error) {
              Swal.fire("Error", "Something went wrong.", "error");
            }
          }
        });
      }
      return;
    }

    if (action === "Toggle 2FA") {
      if (!user.twoFactorEnabled) {
        Swal.fire("Info", "2FA is not enabled for this user. The user must enable it themselves from their settings.", "info");
        return;
      }
      Swal.fire({
        title: `Disable 2FA for ${user.name}?`,
        text: "This will reset their two-factor authentication. The user will need to re-enable it themselves.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f5365c",
        confirmButtonText: "Yes, disable 2FA",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const res = await toggle2FA(user.email);
            if (res.status === true || res.status === "success" || res.status) {
              setUsers(prevUsers => prevUsers.map(u =>
                u._id === user._id ? { ...u, twoFactorEnabled: false } : u
              ));
              Swal.fire({
                title: "Success!",
                text: res.message || "2FA has been disabled for this user.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false
              });
            } else {
              Swal.fire("Error!", res.message || "Failed to disable 2FA.", "error");
            }
          } catch (error) {
            Swal.fire("Error", "Something went wrong.", "error");
          }
        }
      });
      return;
    }

    Swal.fire({
      title: `Are you sure you want to ${action} ${user.name}?`,
      text: `This action will be logged in the security audit trail.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: action === "Block" ? "#f5365c" : "#11cdef",
      confirmButtonText: "Yes, proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          logAuditAction(user._id, action);
        } catch (error) {
          Swal.fire("Error", "Something went wrong.", "error");
        }
      }
    });
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetError("");

    if (!resetData.newPassword) {
      setResetError("Please enter a new password.");
      return;
    }
    if (!resetData.confirmPassword) {
      setResetError("Please confirm your new password.");
      return;
    }
    if (resetData.newPassword !== resetData.confirmPassword) {
      setResetError("Passwords do not match!");
      return;
    }

    setResetLoading(true);

    try {
      const res = await resetUserPasswordAPI({
        email: selectedUser.email,
        newPassword: resetData.newPassword,
        confirmPassword: resetData.confirmPassword
      });

      if (res.status) {
        setResetModalOpen(false);
        setResetData({ newPassword: "", confirmPassword: "", adminPassword: "" });
        setResetError("");

        Swal.fire({
          title: "Success",
          text: "User password has been reset successfully.",
          icon: "success",
          showConfirmButton: false,
          timer: 2000
        });

      } else {
        Swal.fire({
          title: "Error",
          text: res.message || "Failed to reset password",
          icon: "error",
          showConfirmButton: false,
          timer: 2000
        });
      }

    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Server error occurred",
        icon: "error",
        showConfirmButton: false,
        timer: 2000
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setModalOpen(true);
    setModalLoading(true);
    setAuditLogsLoading(true);
    try {
      const res = await getUserProfile(user._id);
      if (res.status) {
        setSelectedUser(res.payload);
      }
      const auditRes = await getAuditLogs({ target_id: user._id, limit: 10 });
      if (auditRes.status === true || auditRes.status === "success") {
        setUserAuditLogs(auditRes.payload?.logs || []);
      }
    } catch (error) {
      console.error("Failed to fetch user details or audit logs:", error);
    } finally {
      setModalLoading(false);
      setAuditLogsLoading(false);
    }
  };
  useEffect(() => {
    const fetchCountries = async () => {
      setIsCountriesLoading(true);
      try {
        const res = await getAllCountries(1, 1000);
        if (res?.status) {
          setCountries(res.payload.countries || []);
        }
      } catch (err) {
        console.error("Failed to fetch countries", err);
      } finally {
        setIsCountriesLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      if (countryFilter === "All") {
        setStates([]);
        return;
      }
      setIsStatesLoading(true);
      try {
        const res = await getAllStates(1, 1000, countryFilter);
        if (res?.status) {
          setStates(res.payload.states || []);
        }
      } catch (err) {
        console.error("Failed to fetch states", err);
      } finally {
        setIsStatesLoading(false);
      }
    };

    fetchStates();
  }, [countryFilter]);

  useEffect(() => {
    const fetchCities = async () => {
      console.log(countryFilter, stateFilter, "countryFilter and stateFilter");
      if (countryFilter === "All") {
        setCities([]);
        return;
      }
      const s_uuid = stateFilter === "All" ? "" : stateFilter;
      setIsCitiesLoading(true);
      try {
        const res = await getAllCities(1, 1000, countryFilter, s_uuid);
        console.log(res, "res");
        if (res?.status) {
          setCities(res.payload.cities || []);
        }
      } catch (err) {
        console.error("Failed to fetch cities", err);
      } finally {
        setIsCitiesLoading(false);
      }
    };

    fetchCities();
  }, [countryFilter, stateFilter]);



  return (
    <>
      <CommonHeader />
      <Container className="mt--7" fluid>
        {/* Filters Row */}
        <Row className="mb-4">
          <Col xl="12">
            <Card className="shadow border-0 px-4 py-3">
              <Row>
                <Col md="2">
                  <FormGroup className="mb-md-0">
                    <Label className="text-sm font-weight-bold">Search</Label>
                    <Input
                      placeholder="Name or Chatusername"
                      type="text"
                      className="form-control-alternative"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup className="mb-md-0">
                    <Label className="text-sm font-weight-bold">Country</Label>
                    <Select
                      value={countryFilter === "All" ? { value: "All", label: "All Countries" } : { value: countryFilter, label: countries.find(c => c.c_uuid === countryFilter)?.c_name }}
                      onChange={opt => {
                        setCountryFilter(opt ? opt.value : "All");
                        setStateFilter("All");
                        setCityFilter("All");
                      }}
                      options={[{ value: "All", label: "All Countries" }, ...countries.map(c => ({ value: c.c_uuid, label: c.c_name }))]}
                      placeholder="Search Country"
                      isSearchable={true}
                      isLoading={isCountriesLoading}
                      isClearable={false}
                      styles={filterSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup className="mb-md-0">
                    <Label className="text-sm font-weight-bold">State</Label>
                    <div style={{ position: "relative" }}>
                      {(!countryFilter || countryFilter === "All") && (
                        <div
                          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 10 }}
                          onClick={() => Swal.fire("Error", "Please first select the Country/State.", "error")}
                        />
                      )}
                      <Select
                        value={stateFilter === "All" ? { value: "All", label: "All States" } : { value: stateFilter, label: states.find(s => s.s_uuid === stateFilter)?.s_name || "Select State" }}
                        onChange={opt => {
                          setStateFilter(opt ? opt.value : "All");
                          setCityFilter("All");
                        }}
                        options={[{ value: "All", label: "All States" }, ...states.map(s => ({ value: s.s_uuid, label: s.s_name }))]}
                        placeholder="Search State"
                        isSearchable={true}
                        isLoading={isStatesLoading}
                        isDisabled={!countryFilter || countryFilter === "All"}
                        isClearable={false}
                        styles={filterSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup className="mb-md-0">
                    <Label className="text-sm font-weight-bold">City</Label>
                    <div style={{ position: "relative" }}>
                      {(!stateFilter || stateFilter === "All") && (
                        <div
                          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 10 }}
                          onClick={() => Swal.fire("Error", "Please first select the Country/State.", "error")}
                        />
                      )}
                      <Select
                        value={
                          cityFilter === "All"
                            ? { value: "All", label: "All Cities" }
                            : {
                              value: cityFilter,
                              label:
                                cities.find((ci) => ci.ci_uuid === cityFilter)?.ci_name ||
                                "Select City",
                            }
                        }
                        onChange={(opt) => setCityFilter(opt ? opt.value : "All")}
                        options={[
                          { value: "All", label: "All Cities" },
                          ...cities.map((ci) => ({
                            value: ci.ci_uuid,
                            label: ci.ci_name,
                          })),
                        ]}
                        placeholder="Search City"
                        isSearchable
                        isLoading={isCitiesLoading}
                        isDisabled={!stateFilter || stateFilter === "All"}
                        isClearable={false}
                        styles={filterSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup className="mb-md-0">
                    <Label className="text-sm font-weight-bold">Reputation Tier</Label>
                    <Select
                      value={tierFilter === "All" ? { value: "All", label: "All Tiers" } : { value: tierFilter, label: tierFilter }}
                      onChange={opt => setTierFilter(opt ? opt.value : "All")}
                      options={[{ value: "All", label: "All Tiers" }, ...reputationTiers.map(tier => ({ value: tier.name, label: tier.name }))]}
                      styles={filterSelectStyles}
                      isSearchable={true}
                      isClearable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <FormGroup className="mb-md-0">
                    <Label className="text-sm font-weight-bold">Status</Label>
                    <Select
                      value={statusFilter === "All" ? { value: "All", label: "All Status" } : { value: statusFilter, label: statusFilter }}
                      onChange={opt => setStatusFilter(opt ? opt.value : "All")}
                      options={[
                        { value: "All", label: "All Status" },
                        { value: "Active", label: "Active" },
                        { value: "Blocked", label: "Blocked" }
                      ]}
                      styles={filterSelectStyles}
                      isSearchable={false}
                      isClearable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row>
          <div className="col">
            <Card className="shadow border-0">
              <CardHeader className="border-0 bg-transparent">
                <Row className="align-items-center">
                  <Col>
                    <h3 className="mb-0 text-dark font-weight-bold">
                      <i className="ni ni-single-02 text-blue mr-2" /> CradleAI - User Directory
                    </h3>
                  </Col>
                </Row>
              </CardHeader>

              {loading ? (
                <div className="text-center p-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></div>
              ) : (
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Avatar</th>
                      <th scope="col">User</th>
                      <th scope="col">Chatusername</th>
                      <th scope="col">Country</th>
                      <th scope="col">State</th>
                      <th scope="col">City</th>
                      <th scope="col">Language</th>
                      <th scope="col">Join Date</th>
                      <th scope="col">Rep Score</th>
                      <th scope="col">Tier</th>
                      <th scope="col">Status</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((user) => (
                        <tr
                          key={user._id}
                          id={`user-row-${user._id}`}
                          className={highlightId === String(user._id) ? "table-row-highlight" : ""}
                          style={{
                            transition: "background-color 0.5s ease"
                          }}
                        >
                          <td>
                            <Avatar
                              src={user.image}
                              name={user.name}
                              size="md"
                            />
                          </td>
                          <th scope="row">
                            <div className="d-flex flex-column">
                              <span className="mb-0 text-sm font-weight-bold text-dark">
                                {user?.name}
                              </span>
                              <span className="text-muted text-xs">
                                {user.email}
                              </span>
                            </div>
                          </th>
                          <td>
                            <div className="text-sm">{user.handlername || "-"}</div>
                          </td>
                          <td>
                            <div className="text-sm">{user.country || "-"}</div>
                          </td>
                          <td>
                            <div className="text-sm">{user.state || "-"}</div>
                          </td>
                          <td>
                            <div className="text-sm">{user.city || "-"}</div>
                          </td>
                          <td>
                            <div className="text-sm" style={{ textTransform: "uppercase" }}>{user.preferredLanguage || "-"}</div>
                          </td>
                          <td>
                            <div className="text-sm">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}</div>
                          </td>

                          <td>
                            <div className="d-flex align-items-center">
                              <span className="mr-2 font-weight-bold" style={{ fontSize: '0.85rem' }}>{user.reputationScore || 0}</span>
                              <div style={{ width: "80px" }}>
                                <div className="progress progress-sm mb-0" style={{ height: '6px', backgroundColor: '#e9ecef', borderRadius: '10px', }}>
                                  <div
                                    className="progress-bar"
                                    style={{
                                      width: `${Math.max(5, Math.min(((user.reputationScore || 0) / 1000) * 100, 100))}%`,
                                      backgroundColor: user.reputationColor || "#cd7f32",
                                      borderRadius: '10px',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge
                              style={{
                                backgroundColor: `${user.reputationColor || "#cd7f32"}15`,
                                color: user.reputationColor || "#cd7f32",
                                border: `1px solid ${user.reputationColor || "#cd7f32"}40`,
                                borderRadius: '30px',
                                padding: '5px 12px',
                                textTransform: 'capitalize',
                                fontWeight: '600',
                                letterSpacing: '0.5px'
                              }}
                            >
                              <i className="fas fa-circle mr-2" style={{ fontSize: '6px', verticalAlign: 'middle' }} />
                              {user.reputationTier || "Bronze"}
                            </Badge>
                          </td>
                          <td>
                            <Badge
                              color={user.is_blocked ? "danger" : user.active === false ? "warning" : "success"}
                              pill
                            >
                              {user.is_blocked ? "Blocked" : user.active === false ? "Inactive" : "Active"}
                            </Badge>
                          </td>
                          <td className="text-right">
                            <UncontrolledDropdown>
                              <DropdownToggle
                                className="btn-icon-only "
                                role="button"
                                size="sm"
                                color=""
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              >

                                <i className="fas fa-ellipsis-v" style={{ color: "#6c757d" }} />
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-arrow shadow border-0" right positionFixed>
                                <DropdownItem onClick={() => handleViewUser(user)}>
                                  <i className="fas fa-id-card text-info mr-2" /> View Profile
                                </DropdownItem>
                                <DropdownItem onClick={() => handleAction("Block", user)}>
                                  <i className={`fas fa-${!user.is_blocked ? "ban" : "check"} text-${!user.is_blocked ? "danger" : "success"} mr-2`} /> {!user.is_blocked ? "Block User" : "Unblock User"}
                                </DropdownItem>
                                <DropdownItem onClick={() => handleAction("Reset Password", user)}>
                                  <i className="fas fa-key text-warning mr-2" /> Reset Password
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() => user.twoFactorEnabled ? handleAction("Toggle 2FA", user) : null}
                                  disabled={!user.twoFactorEnabled}
                                  className={!user.twoFactorEnabled ? "text-muted" : ""}
                                >
                                  <i className={`fas fa-shield-alt ${user.twoFactorEnabled ? "text-danger" : "text-muted"} mr-2`} />
                                  {user.twoFactorEnabled ? "Disable 2FA" : "2FA Not Active"}
                                </DropdownItem>
                                <DropdownItem onClick={() => handleAction("Sessions", user)}>
                                  <i className="fas fa-history text-muted mr-2" /> Active Sessions
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center py-5">
                          <div className="text-muted mb-3">No matching users found</div>
                          <Button color="primary" outline size="sm" onClick={() => {
                            setSearchTerm("");
                            setCountryFilter("All");
                            setStateFilter("All");
                            setCityFilter("All");
                            setTierFilter("All");
                            setStatusFilter("All");
                          }}>
                            Clear All Filters
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}

              <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                <span className="text-muted small">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </span>
                <nav aria-label="...">
                  <Pagination className="pagination justify-content-end mb-0">
                    <PaginationItem disabled={pagination.page <= 1}>
                      <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchUsers(pagination.page - 1); }} tabIndex="-1">
                        <i className="fas fa-angle-left" />
                      </PaginationLink>
                    </PaginationItem>
                    {renderPaginationItems(pagination.page, pagination.total, pagination.limit, fetchUsers)}
                    <PaginationItem disabled={pagination.page >= pagination.totalPages}>
                      <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchUsers(pagination.page + 1); }}>
                        <i className="fas fa-angle-right" />
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </nav>
              </CardFooter>
            </Card>
          </div>
        </Row>
        <SessionModal
          isOpen={sessionModalOpen}
          toggle={toggleSessionModal}
          user={selectedUserForSessions}
        />
      </Container>
      {/* User Profile Drawer (Modal) */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        centered
        size="xl"
        className="modal-dialog-centered"
        zIndex={1060}
      >
        <div className="modal-content border-0">
          {/* Header / Cover */}
          <div
            className="card-header border-0"
            style={{
              minHeight: "220px",
              backgroundImage:
                "url(https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=1000&q=80)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              position: "relative",
              paddingTop: "60px",
              paddingBottom: "20px"
            }}
          >
            <span className="mask bg-gradient-default opacity-4 rounded-top" />

            {/* Close Button Top Right */}
            <Button
              color="white"
              size="sm"
              onClick={() => setModalOpen(false)}
              style={{
                position: "absolute",
                top: "15px",
                right: "15px",
                zIndex: 2
              }}
            >
              Close
            </Button>

            <div
              className="profile-header-content w-100 text-center text-md-left"
              style={{ position: "relative", zIndex: 2 }}
            >
              <div className="d-flex flex-column flex-md-row align-items-center">

                {/* Avatar */}
                <div className="mb-3 mb-md-0">
                  <Avatar
                    src={selectedUser?.image}
                    name={selectedUser?.name}
                    style={{
                      width: "110px",
                      height: "110px",
                      minWidth: "110px",
                      minHeight: "110px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "4px solid white"
                    }}
                  />
                </div>

                {/* User Info */}
                <div className="ml-md-4 text-white">
                  <h4 className="mb-1 font-weight-bold">
                    {selectedUser?.name}
                  </h4>
                  <p className="mb-0">
                    {selectedUser?.email}
                  </p>
                  <p className="mb-0">
                    {selectedUser?.location || "-"}
                  </p>
                </div>

              </div>
            </div>
          </div>

          <ModalBody className="bg-secondary px-4 pt-5" style={{ position: "relative", minHeight: "400px" }}>
            {modalLoading && (
              <div style={{
                position: "absolute",
                top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(248, 249, 254, 0.7)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "0 0 0.375rem 0.375rem"
              }}>
                <i className="fas fa-spinner fa-spin fa-2x text-primary" />
              </div>
            )}

            <Row>

              {/* Left Column: Stats & Info */}
              <Col lg="8">
                {/* 1. Basic Info */}
                <Card className="shadow-sm border-0 mb-4 rounded-lg">
                  <CardBody>
                    <h5 className="text-uppercase text-muted mb-4 font-weight-bold">
                      <i className="fas fa-user-circle mr-2 text-primary" /> Basic Information
                    </h5>
                    <Row>
                      <Col md="6" className="mb-3">
                        <small className="text-muted d-block">Country</small>
                        <span className="font-weight-bold text-dark">{selectedUser?.country || "-"}</span>
                      </Col>
                      <Col md="6" className="mb-3">
                        <small className="text-muted d-block">City</small>
                        <span className="font-weight-bold text-dark">{selectedUser?.city || "-"}</span>
                      </Col>
                      <Col md="6" className="mb-3">
                        <small className="text-muted d-block">Language</small>
                        <span className="font-weight-bold text-dark">{selectedUser?.preferredLanguage?.name || selectedUser?.preferredLanguage || "-"}</span>
                      </Col>
                      <Col md="6" className="mb-3">
                        <small className="text-muted d-block">Join Date</small>
                        <span className="font-weight-bold text-dark">{selectedUser?.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : "-"}</span>
                      </Col>
                      <Col md="6" className="mb-3">
                        <small className="text-muted d-block">Email</small>
                        <span className="font-weight-bold text-dark">{selectedUser?.email || "-"}</span>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* 2. Wallet Summary */}
                <Card className="shadow-sm border-0 mb-4 rounded-lg bg-gradient-info text-white">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="text-uppercase text-white opacity-8 mb-2 font-weight-bold">Wallet balance</h5>
                        <h1 className="text-white mb-0 font-weight-bold">${(selectedUser?.stats?.walletBalance ?? selectedUser?.walletBalance) || "0"}</h1>
                      </div>
                      <div className="icon icon-shape bg-white text-info rounded-circle shadow">
                        <i className="fas fa-wallet" />
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-top border-white-50">
                      {/* <Row>
                        <Col xs="6">
                          <small className="opacity-7 d-block">Total Tipped</small>
                          <span className="font-weight-bold text-sm">$45.00</span>
                        </Col>
                        <Col xs="6" className="text-right">
                          <small className="opacity-7 d-block">Monthly Gain</small>
                          <span className="font-weight-bold text-sm">+$12.50</span>
                        </Col>
                      </Row> */}
                    </div>
                  </CardBody>
                </Card>

                {/* 3. Chat Activity Summary */}
                <Card className="shadow-sm border-0 mb-4 rounded-lg">
                  <CardBody>
                    <h5 className="text-uppercase text-muted mb-4 font-weight-bold">
                      <i className="fas fa-chart-bar mr-2 text-warning" /> Chat Activity Summary (7 Days)
                    </h5>
                    <div className="chart" style={{ height: "200px" }}>
                      <Bar
                        data={{
                          labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                          datasets: [{
                            label: "Messages",
                            data: [45, 82, 35, 120, 95, 210, 180],
                            backgroundColor: "#fb6340",
                            borderRadius: 5
                          }]
                        }}
                        options={{
                          ...chartOptions(),
                          maintainAspectRatio: false,
                          scales: {
                            yAxes: [{ ticks: { display: false }, gridLines: { display: false } }],
                            xAxes: [{ gridLines: { display: false } }]
                          }
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>

                {/* 4. Report History */}
                <Card className="shadow-sm border-0 mb-4 rounded-lg">
                  <CardBody>
                    <h5 className="text-uppercase text-muted mb-4 font-weight-bold">
                      <i className="fas fa-flag mr-2 text-danger" /> Report History
                    </h5>
                    <Table className="align-items-center table-flush" responsive>
                      <thead className="thead-light">
                        <tr>
                          <th>Date</th>
                          <th>Reason</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="text-sm">2024-02-10</td>
                          <td className="text-sm font-weight-bold">Spam Messaging</td>
                          <td><Badge color="info">Resolved</Badge></td>
                        </tr>
                        <tr>
                          <td className="text-sm">2023-11-05</td>
                          <td className="text-sm font-weight-bold">Inappropriate Avatar</td>
                          <td><Badge color="success">Cleared</Badge></td>
                        </tr>
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>

                {/* 5. Security Audit Trail (NEW) */}
                <Card className="shadow-sm border-0 mb-4 rounded-lg">
                  <CardBody>
                    <h5 className="text-uppercase text-muted mb-4 font-weight-bold">
                      <i className="fas fa-history mr-2 text-info" /> Security Audit Trail
                    </h5>
                    <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                      <Table className="align-items-center table-flush" responsive>
                        <thead className="thead-light">
                          <tr>
                            <th>Action</th>
                            <th>Date/Time</th>
                            <th>Admin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditLogsLoading ? (
                            <tr>
                              <td colSpan="3" className="text-center py-4">
                                <i className="fas fa-spinner fa-spin text-muted" /> Loading...
                              </td>
                            </tr>
                          ) : userAuditLogs && userAuditLogs.length > 0 ? (
                            userAuditLogs.map((log) => (
                              <tr key={log.al_uuid}>
                                <td className="text-sm font-weight-bold">{log.al_action}</td>
                                <td className="text-sm">{new Date(log.al_created_at).toLocaleString()}</td>
                                <td>
                                  <Badge style={{ backgroundColor: "#172b4d", color: "#ffffff", padding: "6px 12px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", textTransform: "uppercase" }}>
                                    {log.al_admin_name || "System"}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center py-4 text-muted">No security events found</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>

                {/* 6. Admin Actions (Relocated) */}
                <Card className="shadow-sm border-0 mb-4 rounded-lg">
                  <CardBody>
                    <h5 className="text-uppercase text-muted mb-4 font-weight-bold">
                      <i className="fas fa-tools mr-2 text-danger" /> Admin Actions
                    </h5>
                    <div className="d-flex flex-nowrap align-items-center" style={{ gap: "10px" }}>
                      <Button
                        color={!selectedUser?.is_blocked ? "danger" : "success"}
                        size="sm"
                        className="py-2 px-2 text-nowrap"
                        onClick={() => handleAction("Block", selectedUser)}
                      >
                        <i className={`fas fa-${!selectedUser?.is_blocked ? "ban" : "check"} mr-2`} />
                        {!selectedUser?.is_blocked ? "Block User" : "Unblock User"}
                      </Button>
                      <Button
                        color="warning"
                        size="sm"
                        className="text-white py-2 px-3 text-nowrap"
                        onClick={() => handleAction("Reset Password", selectedUser)}
                      >
                        <i className="fas fa-key mr-2" /> Reset Password
                      </Button>
                      <Button
                        color={selectedUser?.twoFactorEnabled ? "danger" : "secondary"}
                        size="sm"
                        className="py-2 px-3 text-nowrap"
                        onClick={() => handleAction("Toggle 2FA", selectedUser)}
                        disabled={!selectedUser?.twoFactorEnabled}
                      >
                        <i className="fas fa-shield-alt mr-2" /> {selectedUser?.twoFactorEnabled ? "Disable 2FA" : "2FA Not Active"}
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        className="py-2 px-3 text-nowrap"
                        onClick={() => handleAction("Sessions", selectedUser)}
                      >
                        <i className="fas fa-history mr-2" /> Active Sessions
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Right Column: Reputation & Badges */}
              <Col lg="4">
                {/* 1. Reputation Score */}
                <Card className="shadow-sm border-0 mb-4 rounded-lg">
                  <CardBody className="text-center">
                    <h5 className="text-uppercase text-muted mb-4 font-weight-bold">Reputation</h5>
                    <div className="display-3 font-weight-bold text-primary mb-0">{selectedUser?.reputationScore}</div>
                    <Badge color="primary" pill className="mb-4">{selectedUser?.reputationTier}</Badge>

                    <div className="progress-wrapper pt-0">
                      <div className="progress-info">
                        <div className="progress-label">
                          <span>Next Tier Progress</span>
                        </div>
                        <div className="progress-percentage">
                          <span>75%</span>
                        </div>
                      </div>
                      <div className="progress progress-xs">
                        <div className="progress-bar bg-primary" role="progressbar" style={{ width: "75%" }} />
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* 2. Badges */}
                <Card className="shadow-sm border-0 mb-4 rounded-lg">
                  <CardBody>
                    <h5 className="text-uppercase text-muted mb-4 font-weight-bold">Badges</h5>
                    <div className="d-flex flex-wrap justify-content-center">
                      {(selectedUser?.badges || []).map((badge, idx) => (
                        <div key={idx} className="m-2 text-center" style={{ width: "70px" }}>
                          <div className="icon icon-sm icon-shape bg-gradient-yellow text-white rounded-circle shadow mb-2">
                            <i className="fas fa-award" />
                          </div>
                          <small className="d-block text-xs font-weight-bold">{badge}</small>
                        </div>
                      ))}
                      {(!selectedUser?.badges || selectedUser.badges.length === 0) && (
                        <div className="m-2 text-center text-muted">No badges earned yet</div>
                      )}
                      {/* In Progress Badges */}
                      <div className="m-2 text-center opacity-4" style={{ width: "70px" }}>
                        <div className="icon icon-sm icon-shape bg-secondary text-muted rounded-circle shadow mb-2">
                          <i className="fas fa-lock" />
                        </div>
                        <small className="d-block text-xs">Top Tier</small>
                      </div>
                    </div>
                  </CardBody>
                </Card>

              </Col>
            </Row>
          </ModalBody>
        </div>
      </Modal >

      {/* Reset Password Form Modal */}
      <Modal
        isOpen={resetModalOpen}
        toggle={toggleResetModal}
        centered
        size="md"
        zIndex={1070}
      >
        <div className="modal-header bg-warning">
          <h4 className="modal-title text-white mb-0">Reset User Password</h4>
          <button
            aria-label="Close"
            className="close text-white"
            onClick={toggleResetModal}
            type="button"
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <ModalBody className="p-4">
          <p className="text-sm text-muted mb-4 cursor-default">
            You are initiating a password reset for <strong>{selectedUser?.name} ({selectedUser?.email})</strong>. This action will be recorded in the audit logs.
          </p>
          <Form onSubmit={handleResetSubmit} noValidate>
            <FormGroup>
              <Label className="text-sm font-weight-bold">New Password</Label>
              <InputGroup className="input-group-alternative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={resetData.newPassword}
                  onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                  required
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ cursor: "pointer" }}
                  >
                    <i className={`fas fa-eye${showPassword ? "-slash" : ""}`} />
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </FormGroup>
            <FormGroup>
              <Label className="text-sm font-weight-bold">Confirm New Password</Label>
              <InputGroup className="input-group-alternative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={resetData.confirmPassword}
                  onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                  required
                />
                <InputGroupAddon addonType="append">
                  <InputGroupText
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ cursor: "pointer" }}
                  >
                    <i className={`fas fa-eye${showConfirmPassword ? "-slash" : ""}`} />
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </FormGroup>
            <hr className="my-3" />
            {resetError && (
              <div className="text-danger mb-3 text-sm font-weight-bold">
                {resetError}
              </div>
            )}
            <FormGroup>
              {/* <Label className="text-sm font-weight-bold text-danger">Confirm Admin Password</Label>
              <Input
                type="password"
                placeholder="Required for administrative authorization"
                value={resetData.adminPassword}
                onChange={(e) => setResetData({ ...resetData, adminPassword: e.target.value })}
                required
              /> */}
            </FormGroup>
            <div className="text-right">
              <Button color="secondary" onClick={toggleResetModal} disabled={resetLoading}>Cancel</Button>
              <Button color="warning" type="submit" className="text-white ml-2" disabled={resetLoading}>
                {resetLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" /> Resetting...
                  </>
                ) : (
                  "Confirm Reset"
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal >

    </>
  );
};

export default UserTable;
